import type { RestaurantId } from '~/lib/graphql/scalars'
import { css, sva } from '~/styled-system/css'
import { vstack } from '~/styled-system/patterns'
import { CouponDetailList } from './CouponDetailList'
import { calcMaxOffPrice } from './calcMaxOffPrice'
import { useDiscountCoupons } from './query'

type Props = {
  restaurantId: RestaurantId
  detailDirection?: 'horizontal' | 'vertical'
}

const styles = sva({
  slots: ['item'],
  base: {
    item: {
      whiteSpace: 'pre-wrap',
      paddingLeft: '4',
      textIndent: '[-16px]',
    },
  },
})

export function CouponDetailContainer({ restaurantId, detailDirection = 'vertical' }: Props) {
  const coupons = useDiscountCoupons(restaurantId, { invalidate: true })
  const maxOffPrice = calcMaxOffPrice(coupons)
  const classes = styles()
  return (
    <div
      className={css({
        px: '3',
        py: '5',
        spaceY: '5',
        bgColor: 'white',
        _pc: { w: '[600px]', my: '0', mx: 'auto', px: '0', py: '10' },
      })}
    >
      <header
        className={css({
          spaceY: '5',
        })}
      >
        <h2
          className={css({
            whiteSpace: 'pre-wrap',
            textAlign: 'center',
            fontSize: '2xl',
            fontWeight: 'bold',
            color: 'coupon.start',
          })}
        >
          <span className={css({ fontSize: 'lg', _pc: { fontSize: 'xl' } })}>
            {coupons[0].name}
          </span>
          <p className={css({ fontSize: '3xl', _pc: { fontSize: '4xl' } })}>
            {coupons.length > 1 && '最大'}
            {maxOffPrice}円OFFクーポン
          </p>
        </h2>
      </header>
      <div className={css({ spaceY: '3', _pc: { spaceY: '5' } })}>
        <CouponDetailList
          restaurantId={restaurantId}
          coupons={coupons}
          detailDirection={detailDirection}
        />
      </div>
      <div
        className={css({
          spaceY: '3',
          bg: 'gray.100',
          px: '3',
          py: '5',
          rounded: 'md',
          _pc: { p: '6', spaceY: '5' },
        })}
      >
        <section
          className={vstack({ gap: '2', alignItems: 'start', fontSize: 'sm', _pc: { gap: '3' } })}
        >
          <h3 className={css({ fontSize: 'base', fontWeight: 'bold', _pc: { fontSize: 'xl' } })}>
            ご利用方法
          </h3>
          <ul
            className={css({ listStylePosition: 'inside', listStyleType: 'decimal', spaceY: '1' })}
          >
            <li className={classes.item}>
              ご利用する割引クーポンの「獲得する」ボタンをクリックしてください。
              <br />
              ※会員認証（一休会員ログイン）が必要です。
              <br />
              初めてご利用される方は、会員登録（無料）後あらためて、会員認証にお進みください。
            </li>
            <li className={classes.item}>
              予約情報入力画面の「割引クーポン」欄で利用するクーポンを選択してください。
            </li>
            <li className={classes.item}>
              お支払い金額に割引が反映されていることをご確認のうえ、ご予約完了画面にお進みください。
            </li>
          </ul>
          <div className={css({ color: 'gray.700' })}>
            ※ご利用するクーポンがコード型の場合はクーポンコードをコピーしご予約情報入力ページに進んでください。割引クーポン入力欄にクーポンコードが入力され、ご予約金額に割引が反映されていることをご確認のうえ、ご予約完了画面にお進みください。
          </div>
        </section>
        <section
          className={vstack({ gap: '2', alignItems: 'start', fontSize: 'sm', _pc: { gap: '3' } })}
        >
          <h3 className={css({ fontSize: 'base', fontWeight: 'bold', _pc: { fontSize: 'xl' } })}>
            注意事項
          </h3>
          <ul className={css({ listStylePosition: 'inside', listStyleType: 'disc', spaceY: '1' })}>
            <li className={classes.item}>
              割引クーポンのご利用には、一休会員登録、認証(ログイン)が必要です。
            </li>
            <li className={classes.item}>
              席のみ予約、ゲスト予約では割引クーポンは利用できません。
            </li>
            <li className={classes.item}>
              クーポンは必ず有効期限内にご利用ください。有効期限を過ぎますとご利用いただけません。
            </li>
            <li className={classes.item}>
              コメント欄等、割引クーポンの入力欄以外への記載は無効となります。電話予約、来店時のご提示ではご利用いただけませんので注意ください。
            </li>
            <li className={classes.item}>
              一回のご予約につき、1クーポンずつのご利用となり、1グループ、1クーポンとなります。また他のクーポンとの併用は不可となります。
            </li>
            <li className={classes.item}>
              すでにご予約いただいているプラン代金には、クーポンはご利用いただけません。
            </li>
            <li className={classes.item}>
              クーポンをご利用にならなかった場合にも現金による払い戻し、クーポンの返却はできません。
            </li>
            <li className={classes.item}>
              クーポンコードのコピー機能は、最後にコピーされたものがクーポンコード欄に反映されます。ご希望のクーポンが入力されていない場合は再度クーポンコードをコピーしてください。
            </li>
            <li className={classes.item}>
              割引クーポンのご利用に際しては、ポイント／クーポンサービス利用規約が適用されます。規約は
              <a
                href="https://www.ikyu.com/help/howto/waribiki_coupon.aspx#terms"
                target="_blank"
                rel="noreferrer"
                className={css({ color: 'link' })}
              >
                こちら
              </a>
            </li>
          </ul>
        </section>
      </div>
    </div>
  )
}
